import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, Tabs, TabPanel, SearchableSelect } from '@/backoffice/components/ui';
import { useAdminContext } from '@/backoffice/hooks/useAdminContext';
import {
  fetchZones,
  createZone,
  deleteZone,
  fetchRelayPoints,
  createRelayPoint,
  deleteRelayPoint,
} from '@/domains/admin/services/admin.service';
import type { ConfigZone, AdminRelayPoint } from '@/domains/admin/services/admin.service';
import { MapPin, Plus, Trash2, Building2 } from 'lucide-react';

export function AdminInfrastructurePage() {
  const userType = useAdminContext();
  const [zones, setZones] = useState<ConfigZone[]>([]);
  const [relayPoints, setRelayPoints] = useState<AdminRelayPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [newZoneName, setNewZoneName] = useState('');
  const [relayForm, setRelayForm] = useState({ name: '', address: '', zone_id: '', manager_name: '', manager_phone: '', opening_hours: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [z, r] = await Promise.all([fetchZones(), fetchRelayPoints()]);
      setZones(z);
      setRelayPoints(r);
    } catch {
      setError('Impossible de charger les infrastructures.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateZone = async () => {
    if (!newZoneName.trim()) return;
    setSaving(true);
    try {
      await createZone(newZoneName.trim());
      setNewZoneName('');
      await load();
    } catch {
      setError('Échec de la création de la zone.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRelay = async () => {
    if (!relayForm.name.trim() || !relayForm.address.trim()) return;
    setSaving(true);
    try {
      await createRelayPoint({
        name: relayForm.name,
        address: relayForm.address,
        zone_id: relayForm.zone_id ? Number(relayForm.zone_id) : null,
        manager_name: relayForm.manager_name || null,
        manager_phone: relayForm.manager_phone || null,
        opening_hours: relayForm.opening_hours || null,
        is_active: true,
      });
      setRelayForm({ name: '', address: '', zone_id: '', manager_name: '', manager_phone: '', opening_hours: '' });
      await load();
    } catch {
      setError('Échec de la création du point relais.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: 'zones', label: 'Zones', badge: zones.length },
    { key: 'relay', label: 'Points relais', badge: relayPoints.length },
  ];

  return (
    <DashboardLayout userType={userType} title="Infrastructures">
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <Tabs tabs={tabs}>
        <TabPanel>
          <div className="flex items-center gap-3 mb-6">
            <Input placeholder="Nom de la nouvelle zone" value={newZoneName} onChange={(e) => setNewZoneName(e.target.value)} className="max-w-xs" />
            <Button leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreateZone}>Nouvelle zone</Button>
          </div>
          {loading ? (
            <p className="text-center text-gray-500 py-8">Chargement...</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {zones.map((zone) => (
                <Card key={zone.id}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-indigo-600" />
                      <div>
                        <p className="font-medium">{zone.name}</p>
                        <p className="text-xs text-gray-500">ID {zone.id}</p>
                      </div>
                    </div>
                    <button type="button" onClick={async () => { await deleteZone(zone.id); load(); }} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabPanel>
        <TabPanel>
          <Card className="mb-6">
            <h3 className="font-semibold mb-4">Nouveau point relais</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Nom" value={relayForm.name} onChange={(e) => setRelayForm((f) => ({ ...f, name: e.target.value }))} />
              <Input label="Adresse" value={relayForm.address} onChange={(e) => setRelayForm((f) => ({ ...f, address: e.target.value }))} />
              <SearchableSelect
                label="Zone"
                options={zones.map((z) => ({ value: String(z.id), label: z.name }))}
                value={relayForm.zone_id}
                onChange={(v) => setRelayForm((f) => ({ ...f, zone_id: v }))}
                placeholder="Sélectionner"
              />
              <Input label="Responsable" value={relayForm.manager_name} onChange={(e) => setRelayForm((f) => ({ ...f, manager_name: e.target.value }))} />
              <Input label="Téléphone" value={relayForm.manager_phone} onChange={(e) => setRelayForm((f) => ({ ...f, manager_phone: e.target.value }))} />
              <Input label="Horaires" value={relayForm.opening_hours} onChange={(e) => setRelayForm((f) => ({ ...f, opening_hours: e.target.value }))} />
            </div>
            <Button className="mt-4" leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreateRelay}>Ajouter le point relais</Button>
          </Card>
          <div className="grid md:grid-cols-2 gap-4">
            {relayPoints.map((rp) => (
              <Card key={rp.id}>
                <div className="flex justify-between">
                  <div className="flex gap-3">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-medium">{rp.name}</p>
                      <p className="text-sm text-gray-600">{rp.address}</p>
                      <p className="text-xs text-gray-500">{rp.zone_name} · {rp.opening_hours}</p>
                    </div>
                  </div>
                  <button type="button" onClick={async () => { await deleteRelayPoint(rp.id); load(); }} className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </TabPanel>
      </Tabs>
    </DashboardLayout>
  );
}
